import type {
  EditSpecCaptionSegmentDict,
  EditSpecDict,
} from '../../../../generated/nodeDataTypes'

function requireCaptions(spec: Pick<EditSpecDict, 'captions'>): EditSpecCaptionSegmentDict[] {
  if (!spec.captions) throw new Error('EditSpec has no caption track')
  return spec.captions.segments
}

function replaceSegments<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  segments: EditSpecCaptionSegmentDict[],
): T {
  if (!spec.captions) throw new Error('EditSpec has no caption track')
  return { ...spec, captions: { ...spec.captions, segments } }
}

function replaceCaption<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  captionId: string,
  update: (caption: EditSpecCaptionSegmentDict) => EditSpecCaptionSegmentDict,
): T {
  let found = false
  const segments = requireCaptions(spec).map((caption) => {
    if (caption.id !== captionId) return caption
    found = true
    return update(caption)
  })
  if (!found) throw new Error(`Unknown caption ${captionId}`)
  return replaceSegments(spec, segments)
}

export function editCaptionText<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  captionId: string,
  text: string,
): T {
  const normalized = text.trim()
  if (!normalized) throw new Error('Caption text cannot be empty')
  return replaceCaption(spec, captionId, (caption) => {
    const { words: _staleWords, ...captionWithoutWords } = caption
    return { ...captionWithoutWords, text: normalized }
  })
}

export function moveCaption<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  captionId: string,
  startSeconds: number,
): T {
  if (!Number.isFinite(startSeconds) || startSeconds < 0) {
    throw new Error('Caption start must be a non-negative number')
  }
  return replaceCaption(spec, captionId, (caption) => {
    const duration = caption.end_seconds - caption.start_seconds
    const delta = startSeconds - caption.start_seconds
    return {
      ...caption,
      start_seconds: startSeconds,
      end_seconds: startSeconds + duration,
      words: caption.words?.map((word) => ({
        ...word,
        start_seconds: word.start_seconds + delta,
        end_seconds: word.end_seconds + delta,
      })),
    }
  })
}

export function trimCaption<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  captionId: string,
  startSeconds: number,
  endSeconds: number,
): T {
  if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds) || startSeconds < 0) {
    throw new Error('Caption trim must use finite non-negative times')
  }
  if (endSeconds <= startSeconds) throw new Error('Caption end must be after start')
  return replaceCaption(spec, captionId, (caption) => ({
    ...caption,
    start_seconds: startSeconds,
    end_seconds: endSeconds,
    words: caption.words?.filter(
      (word) => word.start_seconds >= startSeconds && word.end_seconds <= endSeconds,
    ),
  }))
}

export function addCaption<T extends Pick<EditSpecDict, 'captions'>>(
  spec: T,
  startSeconds: number,
  id: string,
  text: string,
): T {
  if (!spec.captions) throw new Error('EditSpec has no caption track')
  if (!Number.isFinite(startSeconds) || startSeconds < 0) {
    throw new Error('Caption start must be a non-negative number')
  }
  if (spec.captions.segments.some((caption) => caption.id === id)) {
    throw new Error(`Duplicate caption id ${id}`)
  }
  return replaceSegments(
    spec,
    [...spec.captions.segments, {
      id,
      text,
      start_seconds: startSeconds,
      end_seconds: startSeconds + 2,
    }].sort((left, right) => left.start_seconds - right.start_seconds),
  )
}

export function deleteCaption<T extends Pick<EditSpecDict, 'captions'>>(spec: T, captionId: string): T {
  const segments = requireCaptions(spec)
  if (!segments.some((caption) => caption.id === captionId)) {
    throw new Error(`Unknown caption ${captionId}`)
  }
  return replaceSegments(
    spec,
    segments.filter((caption) => caption.id !== captionId),
  )
}
